import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  fetchCommunity, joinCommunity, leaveCommunity, fetchCommunityMembers,
  fetchCommunityMedia, fetchCommunityAnalytics, updateCommunity,
  setCommunityRole, kickCommunityMember,
  pinCommunityPost, featureCommunityPost, hideCommunityPost,
} from "@/lib/api";
import { useAuth, authClient, formatApiError } from "@/lib/auth";
import { sfx } from "@/lib/sounds";
import PostComposer from "@/components/PostComposer";
import PostCard from "@/components/PostCard";
import {
  Users, Star, Shield, Crown, ArrowLeft, Loader2, Pin, Sparkles, EyeOff,
  Image as ImageIcon, BarChart2, ListChecks, MessageSquare, Settings, X as XIcon,
} from "lucide-react";

function Stat({ label, value }) {
  return (
    <div className="rounded-xl glass p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] text-white/50">{label}</div>
      <div className="font-hero text-2xl mt-1">{value}</div>
    </div>
  );
}

function RoleBadge({ role }) {
  if (role === "owner") return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#FFBF00]"><Crown size={11} /> Owner</span>;
  if (role === "mod")   return <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#3ABEFF]"><Shield size={11} /> Mod</span>;
  return null;
}

function MembersPanel({ slug, isOwner, isMod, currentUserId }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => fetchCommunityMembers(slug, { limit: 100 }).then(setRows).catch(() => {}), [slug]);
  useEffect(() => { load(); }, [load]);

  const changeRole = async (uid, role) => {
    try { await setCommunityRole(slug, uid, role); sfx.success(); toast.success(`Role set to ${role}`); load(); }
    catch (e) { sfx.error(); toast.error(formatApiError(e)); }
  };
  const kick = async (uid) => {
    if (!window.confirm("Remove this member from the community?")) return;
    try { await kickCommunityMember(slug, uid); sfx.no(); toast.success("Member removed"); load(); }
    catch (e) { sfx.error(); toast.error(formatApiError(e)); }
  };

  return (
    <div className="rounded-2xl glass overflow-hidden" data-testid="community-members-panel">
      <div className="p-4 border-b border-white/10 text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">Members · {rows.length}</div>
      <div className="divide-y divide-white/5 max-h-[70vh] overflow-y-auto">
        {rows.map((m) => (
          <div key={m.id} className="flex items-center gap-3 p-3" data-testid={`community-member-${m.id}`}>
            <Link to={`/users/${m.id}`}>
              <img src={m.avatar || `https://mc-heads.net/avatar/${m.mc_username || m.name || "Steve"}/64`} alt="" className="w-10 h-10 rounded-lg" />
            </Link>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 truncate">
                <Link to={`/users/${m.id}`} className="font-semibold truncate hover:text-[#FFBF00]">{m.name || m.mc_username}</Link>
                <RoleBadge role={m.role} />
              </div>
              <div className="text-[11px] text-white/40">{m.mc_username && `MC: ${m.mc_username} · `}joined {(m.joined_at || "").slice(0, 10)}</div>
            </div>
            {(isMod || isOwner) && m.id !== currentUserId && m.role !== "owner" && (
              <div className="flex items-center gap-1.5">
                {isOwner && m.role !== "mod" && (
                  <button onClick={() => changeRole(m.id, "mod")} data-testid={`role-promote-${m.id}`}
                          className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-[#3ABEFF]/40 text-[#3ABEFF] hover:bg-[#3ABEFF]/10">
                    Promote
                  </button>
                )}
                {isOwner && m.role === "mod" && (
                  <button onClick={() => changeRole(m.id, "member")} data-testid={`role-demote-${m.id}`}
                          className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-white/20 hover:border-white/50">
                    Demote
                  </button>
                )}
                <button onClick={() => kick(m.id)} data-testid={`kick-${m.id}`}
                        className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-red-400/40 text-red-300 hover:bg-red-500/10">
                  Kick
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function MediaPanel({ slug }) {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { fetchCommunityMedia(slug).then(setMedia).catch(() => setMedia([])).finally(() => setLoading(false)); }, [slug]);

  if (loading) return <div className="rounded-2xl glass p-8 text-center text-white/50"><Loader2 className="animate-spin mx-auto" /></div>;
  if (media.length === 0) return <div className="rounded-2xl glass p-8 text-center text-white/50" data-testid="media-empty">No media yet — post an image to fill the gallery.</div>;

  return (
    <div className="rounded-2xl glass p-4" data-testid="community-media-panel">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00] mb-3">Gallery · {media.length}</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {media.map((m, i) => (
          <a key={`${m.post_id}-${i}`} href={m.src} target="_blank" rel="noreferrer" data-testid={`media-item-${i}`}
             className="aspect-square rounded-lg overflow-hidden glass hover:border-[#FFBF00]/40 group">
            <img src={m.src} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
          </a>
        ))}
      </div>
    </div>
  );
}

function AnalyticsPanel({ slug }) {
  const [a, setA] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => { fetchCommunityAnalytics(slug).then(setA).catch((e) => setErr(formatApiError(e))); }, [slug]);
  if (err) return <div className="rounded-2xl glass p-6 text-white/60" data-testid="analytics-forbidden">{err}</div>;
  if (!a)  return <div className="rounded-2xl glass p-8 text-center"><Loader2 className="animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4" data-testid="community-analytics-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Members" value={a.member_count} />
        <Stat label="Posts" value={a.post_count} />
        <Stat label="Pinned" value={a.posts_pinned} />
        <Stat label="Featured" value={a.posts_featured} />
        <Stat label="Hidden" value={a.posts_hidden} />
      </div>
      <div className="rounded-2xl glass p-4">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00] mb-3">Posts / day (last 14)</div>
        <div className="flex items-end gap-1 h-32">
          {Object.entries(a.posts_per_day || {}).slice(-14).map(([day, n]) => {
            const max = Math.max(1, ...Object.values(a.posts_per_day || { x: 1 }));
            const h = Math.max(6, Math.round((n / max) * 128));
            return (
              <div key={day} className="flex-1 flex flex-col items-center justify-end gap-1 min-w-0">
                <div className="w-full bg-[#FFBF00] rounded-t-sm" style={{ height: `${h}px` }} title={`${day}: ${n}`} />
                <div className="text-[9px] text-white/40">{day.slice(5)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function AboutEditor({ community, onSaved, isPlatformAdmin }) {
  const [name, setName] = useState(community.name || "");
  const [description, setDescription] = useState(community.description || "");
  const [category, setCategory] = useState(community.category || "");
  const [banner, setBanner] = useState(community.banner || "");
  const [avatar, setAvatar] = useState(community.avatar || "");
  const [rules, setRules] = useState((community.rules || []).join("\n"));
  const [isPrivate, setIsPrivate] = useState(!!community.is_private);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const upd = await updateCommunity(community.slug, {
        name, description, category, banner, avatar,
        rules: rules.split("\n").map(s => s.trim()).filter(Boolean),
        is_private: isPrivate,
      });
      sfx.success(); toast.success("Community updated"); onSaved?.(upd);
    } catch (e) { sfx.error(); toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const toggleFeatured = async () => {
    try {
      await authClient.post(`/communities/${community.slug}/feature`, { featured: !community.featured });
      sfx.success(); toast.success(community.featured ? "Unfeatured" : "Featured");
      onSaved?.();
    } catch (e) { sfx.error(); toast.error(formatApiError(e)); }
  };

  return (
    <div className="rounded-2xl glass p-6 space-y-3 max-w-2xl" data-testid="community-editor">
      <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">Edit Community</div>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name" className="w-full bg-black/60 border border-white/10 rounded-md px-3 py-2 text-sm" />
      <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" className="w-full bg-black/60 border border-white/10 rounded-md px-3 py-2 text-sm" />
      <div className="grid grid-cols-2 gap-2">
        <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Category" className="bg-black/60 border border-white/10 rounded-md px-3 py-2 text-sm" />
        <input value={avatar} onChange={(e) => setAvatar(e.target.value)} placeholder="Avatar URL" className="bg-black/60 border border-white/10 rounded-md px-3 py-2 text-sm" />
      </div>
      <input value={banner} onChange={(e) => setBanner(e.target.value)} placeholder="Banner URL" className="w-full bg-black/60 border border-white/10 rounded-md px-3 py-2 text-sm" />
      <textarea rows={4} value={rules} onChange={(e) => setRules(e.target.value)} placeholder="Rules (one per line)" className="w-full bg-black/60 border border-white/10 rounded-md px-3 py-2 text-sm" />
      <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="community-private-toggle">
        <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} className="accent-[#FFBF00]" />
        <span>Private community (members-only visibility)</span>
      </label>
      <div className="flex items-center gap-2 flex-wrap pt-2">
        <button onClick={save} disabled={busy} data-testid="community-save-btn"
                className="px-5 py-2 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold">
          {busy ? "Saving…" : "Save"}
        </button>
        {isPlatformAdmin && (
          <button onClick={toggleFeatured} data-testid="community-feature-toggle"
                  className={`px-5 py-2 rounded-full text-xs uppercase tracking-widest font-semibold ${
                    community.featured ? "border border-white/20 hover:border-red-400/40 text-white/80" : "bg-[#3ABEFF] text-black hover:bg-[#7ad3ff]"
                  }`}>
            {community.featured ? "Unfeature (Admin)" : "Feature (Admin)"}
          </button>
        )}
      </div>
    </div>
  );
}

const TABS = [
  { key: "feed",     label: "Feed",     icon: MessageSquare },
  { key: "members",  label: "Members",  icon: Users },
  { key: "media",    label: "Media",    icon: ImageIcon },
  { key: "about",    label: "About",    icon: ListChecks },
];

export default function CommunityDetail() {
  const { slug } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [c, setC] = useState(null);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState("feed");
  const [busy, setBusy] = useState(false);
  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);

  const isOwner = c && user && c.owner_id === user.id;
  const isMember = !!c?.viewer_is_member;
  const isMod = c?.viewer_role === "mod" || isOwner || user?.role === "admin";

  const load = useCallback(async () => {
    setErr(null);
    try {
      const d = await fetchCommunity(slug);
      setC(d);
      document.title = `${d.name} · LostDC Communities`;
    } catch (e) { setErr(formatApiError(e)); }
  }, [slug]);
  useEffect(() => { load(); return () => { document.title = "LostDC Network"; }; }, [load]);

  const loadingRef = useRef(false);
  const cursorRef = useRef(null);

  const loadPosts = useCallback(async (reset = true) => {
    if (!c) return;
    if (loadingRef.current) return;
    loadingRef.current = true;
    setPostsLoading(true);
    try {
      const params = new URLSearchParams({ community_id: c.id, limit: "20" });
      if (!reset && cursorRef.current) params.set("cursor", cursorRef.current);
      const { data } = await authClient.get(`/feed?${params.toString()}`);
      cursorRef.current = data.next_cursor || null;
      setNextCursor(cursorRef.current);
      setPosts((prev) => reset ? data.posts : [...prev, ...data.posts]);
    } finally {
      loadingRef.current = false;
      setPostsLoading(false);
    }
  }, [c]);

  useEffect(() => { if (c) { cursorRef.current = null; loadPosts(true); } /* eslint-disable-next-line */ }, [c?.id]);

  // Infinite scroll — listener re-bound only when tab or loader identity changes
  useEffect(() => {
    if (tab !== "feed") return () => {};
    const onScroll = () => {
      if (!cursorRef.current || loadingRef.current) return;
      const near = window.innerHeight + window.scrollY >= document.body.offsetHeight - 800;
      if (near) loadPosts(false);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [tab, loadPosts]);

  const doJoin = async () => {
    if (!user) return toast.error("Please sign in first");
    setBusy(true);
    try {
      if (isMember) { await leaveCommunity(slug); sfx.no(); toast.success("Left community"); }
      else { await joinCommunity(slug); sfx.pling(); toast.success(`Joined ${c.name}`); }
      await load();
    } catch (e) { sfx.error(); toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const modAction = async (post, action) => {
    try {
      if (action === "pin") { const r = await pinCommunityPost(slug, post.id); toast.success(r.pinned ? "Pinned" : "Unpinned"); }
      else if (action === "feature") { const r = await featureCommunityPost(slug, post.id); toast.success(r.featured ? "Featured" : "Unfeatured"); }
      else if (action === "hide") { if (!window.confirm("Hide this post?")) return; await hideCommunityPost(slug, post.id); toast.success("Post hidden"); }
      loadPosts(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (err) return <div className="pt-40 pb-24 px-6 max-w-3xl mx-auto text-center" data-testid="community-error">
    <div className="font-hero text-4xl">Community not found</div>
    <div className="text-white/50 text-sm mt-2">{err}</div>
    <Link to="/communities" className="inline-flex items-center gap-2 mt-6 px-4 py-2 rounded-full border border-white/20 hover:border-[#FFBF00]/40 text-xs uppercase tracking-widest"><ArrowLeft size={12} /> All communities</Link>
  </div>;
  if (!c) return <div className="pt-40 pb-24 px-6 text-center text-white/50" data-testid="community-loading"><Loader2 className="animate-spin mx-auto" /></div>;

  const sortedPosts = posts.slice().sort((a, b) => (Number(b.pinned) - Number(a.pinned)) || (b.created_at || "").localeCompare(a.created_at || ""));

  return (
    <div className="pb-24" data-testid={`page-community-${c.slug}`}>
      {/* Banner */}
      <div className="relative h-48 sm:h-64 overflow-hidden">
        {c.banner ? <img src={c.banner} alt="" className="w-full h-full object-cover" /> : (
          <div className="w-full h-full bg-gradient-to-br from-[#FFBF00]/20 via-black to-[#FF8C00]/10" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        <Link to="/communities" className="absolute top-24 left-4 sm:left-6 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full glass text-xs uppercase tracking-widest hover:border-[#FFBF00]/40">
          <ArrowLeft size={12} /> Communities
        </Link>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 -mt-14 relative z-10">
        <div className="flex flex-col sm:flex-row items-start gap-4">
          {c.avatar ? (
            <img src={c.avatar} alt="" className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl border-4 border-black object-cover" />
          ) : (
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl border-4 border-black bg-gradient-to-br from-[#FFBF00] to-[#FF8C00] grid place-items-center text-black font-hero text-3xl">
              {c.name?.[0]?.toUpperCase() || "C"}
            </div>
          )}
          <div className="flex-1 pt-2">
            <div className="flex items-center gap-2 flex-wrap">
              {c.featured && <span className="px-2 py-0.5 rounded-full bg-[#FFBF00] text-black text-[10px] uppercase tracking-widest font-bold">★ Featured</span>}
              {c.category && <span className="px-2 py-0.5 rounded-full bg-black/60 text-white/70 text-[10px] uppercase tracking-widest">{c.category}</span>}
            </div>
            <h1 className="font-hero text-3xl sm:text-4xl mt-1">{c.name}</h1>
            <p className="text-sm text-white/60 mt-1 max-w-2xl">{c.description}</p>
            <div className="mt-2 text-xs text-white/50 flex items-center gap-3">
              <span className="inline-flex items-center gap-1"><Users size={12} /> {c.member_count} members</span>
              <span>·</span>
              <span>{c.post_count} posts</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={doJoin} disabled={busy} data-testid={isMember ? "community-leave-btn" : "community-join-btn"}
                    className={`px-5 py-2.5 rounded-full text-xs uppercase tracking-widest font-semibold ${
                      isMember ? "border border-white/20 hover:border-red-400/40 text-white/80"
                                : "bg-[#FFBF00] hover:bg-[#FF8C00] text-black"
                    } disabled:opacity-60`}>
              {isMember ? "Leave" : "Join"}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-6 flex items-center gap-2 border-b border-white/10 overflow-x-auto scrollbar-none" data-testid="community-tabs">
          {TABS.concat(isMod ? [{ key: "analytics", label: "Analytics", icon: BarChart2 }] : [])
               .concat((isOwner || user?.role === "admin") ? [{ key: "settings", label: "Settings", icon: Settings }] : [])
               .map(({ key, label, icon: I }) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`community-tab-${key}`}
                    className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-[11px] uppercase tracking-widest whitespace-nowrap border-b-2 transition ${
                      tab === key ? "border-[#FFBF00] text-[#FFBF00]" : "border-transparent text-white/50 hover:text-white"
                    }`}>
              <I size={12} /> {label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "feed" && (
            <div className="grid md:grid-cols-[1fr_260px] gap-6">
              <div className="space-y-4">
                {isMember && user && <PostComposer communityId={c.id} communityName={c.name} onPosted={() => { loadPosts(true); load(); }} />}
                {sortedPosts.map((p) => (
                  <div key={p.id} className="relative" data-testid={`community-post-${p.id}`}>
                    {p.pinned && <div className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FFBF00] text-black text-[9px] uppercase tracking-widest font-bold"><Pin size={10} /> Pinned</div>}
                    {p.featured && <div className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#3ABEFF] text-black text-[9px] uppercase tracking-widest font-bold" style={{marginTop: p.pinned ? '28px' : 0}}><Sparkles size={10} /> Featured</div>}
                    <PostCard post={p} onChanged={() => loadPosts(true)} />
                    {isMod && (
                      <div className="mt-2 flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/50">
                        <button onClick={() => modAction(p, "pin")} data-testid={`mod-pin-${p.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-white/10 hover:border-[#FFBF00]/40"><Pin size={10} /> {p.pinned ? "Unpin" : "Pin"}</button>
                        <button onClick={() => modAction(p, "feature")} data-testid={`mod-feature-${p.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-white/10 hover:border-[#3ABEFF]/40"><Sparkles size={10} /> {p.featured ? "Unfeature" : "Feature"}</button>
                        <button onClick={() => modAction(p, "hide")} data-testid={`mod-hide-${p.id}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-white/10 hover:border-red-400/40 text-red-300"><EyeOff size={10} /> Hide</button>
                      </div>
                    )}
                  </div>
                ))}
                {postsLoading && <div className="text-center text-white/40 py-6"><Loader2 className="animate-spin mx-auto" /></div>}
                {!postsLoading && posts.length === 0 && (
                  <div className="rounded-2xl glass p-12 text-center text-white/50" data-testid="community-empty-feed">
                    <Sparkles className="mx-auto opacity-40 mb-2" />
                    No posts yet. {isMember ? "Kick things off with a post above." : "Join to start posting."}
                  </div>
                )}
                {!nextCursor && posts.length > 0 && (
                  <div className="text-center text-[10px] uppercase tracking-widest text-white/30 py-4">— end of feed —</div>
                )}
              </div>
              <aside className="hidden md:block space-y-4">
                <div className="rounded-2xl glass p-4">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00] mb-3">Top Members</div>
                  <div className="space-y-2">
                    {(c.top_members || []).slice(0, 10).map((m) => (
                      <Link key={m.id} to={`/users/${m.id}`} className="flex items-center gap-2 group">
                        <img src={m.avatar || `https://mc-heads.net/avatar/${m.mc_username || m.name || "Steve"}/64`} alt="" className="w-7 h-7 rounded-md" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm truncate group-hover:text-[#FFBF00]">{m.name || m.mc_username}</div>
                          <RoleBadge role={m.role} />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
                {(c.rules || []).length > 0 && (
                  <div className="rounded-2xl glass p-4">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00] mb-3">Rules</div>
                    <ol className="space-y-2 text-xs text-white/70 list-decimal list-inside">
                      {c.rules.map((r, i) => <li key={`r-${i}`}>{r}</li>)}
                    </ol>
                  </div>
                )}
              </aside>
            </div>
          )}
          {tab === "members" && <MembersPanel slug={c.slug} isOwner={isOwner} isMod={isMod} currentUserId={user?.id} />}
          {tab === "media" && <MediaPanel slug={c.slug} />}
          {tab === "about" && (
            <div className="rounded-2xl glass p-6 space-y-4 max-w-3xl">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">About</div>
              <div className="text-sm text-white/80 whitespace-pre-wrap">{c.description || "No description."}</div>
              {(c.rules || []).length > 0 && (
                <>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFBF00]">Rules</div>
                  <ol className="space-y-2 text-sm text-white/80 list-decimal list-inside">{c.rules.map((r, i) => <li key={`ar-${i}`}>{r}</li>)}</ol>
                </>
              )}
            </div>
          )}
          {tab === "analytics" && <AnalyticsPanel slug={c.slug} />}
          {tab === "settings" && <AboutEditor community={c} onSaved={() => load()} isPlatformAdmin={user?.role === "admin"} />}
        </div>
      </div>
    </div>
  );
}
